import React, { useEffect, useMemo, useRef, useState } from 'react';
import useTrackingStore from '../tracking/useTrackingStore';
import IncidentPicker from '../tracking/IncidentPicker';
import { activeDeploymentsForPerson, personNamesMatch } from '../tracking/deploymentUsageModel';
import { getMemberAccessToken } from '../auth/supabase';
import {
  defaultDaily,
  formatIncidentDisplay,
  latestDaily,
  load214State,
  localDateInput,
  localTimeInput,
  make214Id,
  normalizeIncident,
  save214State,
} from './ics214Store';
import {
  generateIcs214Pdf,
  ics214FileName,
  pdfBytesToBase64,
  shareOrOpenIcs214,
} from './ics214Pdf';
import './ics214.css';

function memberId(profile) {
  return String(profile?.id || profile?.email || profile?.full_name || 'member').trim();
}

function nowLocalDateTime() {
  const now = new Date();
  return `${localDateInput(now)}T${localTimeInput(now)}`;
}

function formatWhen(value) {
  const raw = String(value || '').trim();
  const date = new Date(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(raw) ? `${raw}:00` : raw);
  if (Number.isNaN(date.getTime())) return raw;
  return date.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function defaultSubject(incident, daily) {
  const op = String(daily?.opLabel || '').trim();
  return ['ICS 214', incident?.name, op, daily?.name].filter(Boolean).join(' - ');
}

function invalidate214Signature(daily, updates = {}) {
  return {
    ...daily,
    ...updates,
    signatureStrokes: [],
    signedAt: '',
  };
}

function signatureHasInk(strokes) {
  return Array.isArray(strokes) && strokes.some(stroke => Array.isArray(stroke) && stroke.length >= 2);
}

function drawSignatureCanvas(canvas, strokes) {
  if (!canvas) return;
  const context = canvas.getContext('2d');
  if (!context) return;
  context.clearRect(0, 0, canvas.width, canvas.height);
  context.lineWidth = 4;
  context.lineCap = 'round';
  context.lineJoin = 'round';
  context.strokeStyle = '#111827';

  (strokes || []).forEach(stroke => {
    if (!Array.isArray(stroke) || !stroke.length) return;
    context.beginPath();
    stroke.forEach((point, index) => {
      const x = Math.max(0, Math.min(1, Number(point?.x) || 0)) * canvas.width;
      const y = Math.max(0, Math.min(1, Number(point?.y) || 0)) * canvas.height;
      if (index === 0) context.moveTo(x, y);
      else context.lineTo(x, y);
    });
    context.stroke();
  });
}

function incidentOptions(feed, trackingState) {
  const rows = [];
  const seen = new Set();
  const add = value => {
    const name = String(value?.name || value?.incident || '').trim();
    if (!name) return;
    const incidentOrderNumber = String(value?.incidentOrderNumber || '').trim().toUpperCase();
    const display = incidentOrderNumber ? `${name} — ${incidentOrderNumber}` : name;
    const key = display.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    rows.push({
      name,
      incidentOrderNumber,
      incidentIrwinId: String(value?.incidentIrwinId || value?.irwinId || '').trim(),
      incidentSource: String(value?.incidentSource || value?.source || '').trim(),
      display,
    });
  };
  (feed || []).forEach(add);
  (trackingState?.deployments || []).forEach(add);
  (trackingState?.recentIncidents || []).forEach(name => add({ name }));
  return rows;
}

function ResourceEditor({ resources, onChange, daily, profile }) {
  const max = 5;
  const addSelf = () => {
    if (resources.some(item => personNamesMatch(item.name, profile?.full_name))) return;
    if (resources.length >= max) return;
    onChange([
      ...resources,
      {
        id: make214Id('resource'),
        name: String(profile?.full_name || '').trim(),
        icsPosition: String(daily?.icsPosition || '').trim(),
        homeAgency: String(daily?.homeAgency || '').trim(),
      },
    ]);
  };

  const addBlank = () => {
    if (resources.length >= max) return;
    onChange([...resources, { id: make214Id('resource'), name: '', icsPosition: '', homeAgency: '' }]);
  };

  const update = (id, field, value) => onChange(resources.map(item => item.id === id ? { ...item, [field]: value } : item));
  const remove = id => onChange(resources.filter(item => item.id !== id));

  return (
    <div className="ics214-resources">
      <div className="ics214-row-head">
        <div>
          <strong>Resources Assigned</strong>
          <small>Up to five people total. Add yourself with one tap, then add anyone working with you.</small>
        </div>
        <div className="ics214-inline-actions">
          <button type="button" onClick={addSelf} disabled={resources.length >= max || resources.some(item => personNamesMatch(item.name, profile?.full_name))}>Add Myself</button>
          <button type="button" onClick={addBlank} disabled={resources.length >= max}>+ Add Resource</button>
        </div>
      </div>

      {!resources.length && <div className="ics214-empty-small">No resources added yet.</div>}
      {resources.map((resource, index) => (
        <div className="ics214-resource-card" key={resource.id}>
          <div className="ics214-resource-number">{index + 1}</div>
          <label>Name<input value={resource.name} onChange={event => update(resource.id, 'name', event.target.value)} /></label>
          <label>ICS Position<input value={resource.icsPosition} onChange={event => update(resource.id, 'icsPosition', event.target.value)} /></label>
          <label>Home Agency / Unit<input value={resource.homeAgency} onChange={event => update(resource.id, 'homeAgency', event.target.value)} /></label>
          <button type="button" className="ics214-text-button danger" onClick={() => remove(resource.id)}>Remove</button>
        </div>
      ))}
    </div>
  );
}

function ActivityEditor({ daily, onChange }) {
  const [editingId, setEditingId] = useState('');
  const [at, setAt] = useState(nowLocalDateTime());
  const [description, setDescription] = useState('');

  const reset = () => {
    setEditingId('');
    setAt(nowLocalDateTime());
    setDescription('');
  };

  const submit = event => {
    event.preventDefault();
    const note = description.trim();
    if (!note) return;

    const now = new Date().toISOString();
    if (editingId) {
      onChange((daily.activities || []).map(item => item.id === editingId ? { ...item, at, description: note, updatedAt: now } : item));
    } else {
      onChange([...(daily.activities || []), { id: make214Id('activity'), at, description: note, createdAt: now, updatedAt: now }]);
    }
    reset();
  };

  const edit = item => {
    setEditingId(item.id);
    setAt(item.at || nowLocalDateTime());
    setDescription(item.description || '');
  };

  const remove = id => {
    if (!window.confirm('Remove this activity from the 214?')) return;
    onChange((daily.activities || []).filter(item => item.id !== id));
    if (editingId === id) reset();
  };

  const activities = [...(daily.activities || [])].sort((a, b) => String(a.at || '').localeCompare(String(b.at || '')));

  return (
    <section className="ics214-card">
      <div className="ics214-section-head">
        <div>
          <span className="ics214-kicker">ACTIVITY LOG</span>
          <h3>Notable Activities</h3>
          <p>Add entries as the day happens. The current time is filled in automatically, but you can change it.</p>
        </div>
      </div>

      <form className="ics214-activity-form" onSubmit={submit}>
        <label>Date / Time<input type="datetime-local" value={at} onChange={event => setAt(event.target.value)} required /></label>
        <label className="ics214-activity-note">Activity<textarea value={description} onChange={event => setDescription(event.target.value)} placeholder="Briefly document the notable activity, assignment, completion, issue, communication, etc." required /></label>
        <div className="ics214-inline-actions">
          <button type="submit" className="ics214-primary">{editingId ? 'Save Activity' : '+ Add Activity'}</button>
          {editingId && <button type="button" onClick={reset}>Cancel Edit</button>}
        </div>
      </form>

      {!activities.length && <div className="ics214-empty-small">No activities entered yet.</div>}
      <div className="ics214-activity-list">
        {activities.map(item => (
          <article key={item.id}>
            <time>{formatWhen(item.at)}</time>
            <p>{item.description}</p>
            <div>
              <button type="button" className="ics214-text-button" onClick={() => edit(item)}>Edit</button>
              <button type="button" className="ics214-text-button danger" onClick={() => remove(item.id)}>Delete</button>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}


function SignaturePad({ value, signedAt, onSave, onClear }) {
  const canvasRef = useRef(null);
  const draftRef = useRef(Array.isArray(value) ? value : []);
  const drawingRef = useRef(false);
  const pointerIdRef = useRef(null);
  const [, setRevision] = useState(0);

  useEffect(() => {
    draftRef.current = Array.isArray(value)
      ? value.map(stroke => Array.isArray(stroke) ? stroke.map(point => ({ x: point.x, y: point.y })) : [])
      : [];
    drawSignatureCanvas(canvasRef.current, draftRef.current);
    setRevision(current => current + 1);
  }, [value, signedAt]);

  const pointForEvent = event => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect?.width || !rect?.height) return null;
    return {
      x: Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width)),
      y: Math.max(0, Math.min(1, (event.clientY - rect.top) / rect.height)),
    };
  };

  const redraw = () => drawSignatureCanvas(canvasRef.current, draftRef.current);

  const start = event => {
    event.preventDefault();
    const point = pointForEvent(event);
    if (!point) return;
    drawingRef.current = true;
    pointerIdRef.current = event.pointerId;
    canvasRef.current?.setPointerCapture?.(event.pointerId);
    draftRef.current = [...draftRef.current, [point]];
    redraw();
  };

  const move = event => {
    if (!drawingRef.current || pointerIdRef.current !== event.pointerId) return;
    event.preventDefault();
    const point = pointForEvent(event);
    if (!point) return;
    const strokes = draftRef.current;
    const lastIndex = strokes.length - 1;
    if (lastIndex < 0) return;
    strokes[lastIndex] = [...strokes[lastIndex], point];
    redraw();
  };

  const finish = event => {
    if (pointerIdRef.current !== event.pointerId) return;
    event.preventDefault();
    drawingRef.current = false;
    pointerIdRef.current = null;
    canvasRef.current?.releasePointerCapture?.(event.pointerId);
    setRevision(current => current + 1);
  };

  const clearDraft = () => {
    draftRef.current = [];
    drawSignatureCanvas(canvasRef.current, []);
    setRevision(current => current + 1);
    onClear();
  };

  const save = () => {
    if (!signatureHasInk(draftRef.current)) return;
    onSave(draftRef.current.map(stroke => stroke.map(point => ({ x: point.x, y: point.y }))));
  };

  const hasDraft = signatureHasInk(draftRef.current);
  const isSaved = signatureHasInk(value) && Boolean(signedAt);

  return (
    <section className="ics214-card">
      <div className="ics214-section-head">
        <div>
          <span className="ics214-kicker">PREPARED BY</span>
          <h3>Signature</h3>
          <p>Sign this Daily 214 with your finger. The signature is stored on this device and drawn into the PDF. Editing the header, crew, or activities clears the saved signature so the updated 214 must be signed again.</p>
        </div>
        {isSaved && <div className="ics214-lock-pill locked">Signed</div>}
      </div>

      <div className="ics214-signature-wrap">
        <canvas
          ref={canvasRef}
          className="ics214-signature-canvas"
          width="900"
          height="260"
          aria-label="ICS 214 signature pad"
          onPointerDown={start}
          onPointerMove={move}
          onPointerUp={finish}
          onPointerCancel={finish}
        />
        <small>{isSaved ? `Signed ${formatWhen(signedAt)}` : 'Use your finger to sign inside the box.'}</small>
      </div>

      <div className="ics214-inline-actions">
        <button type="button" className="ics214-primary" disabled={!hasDraft} onClick={save}>
          {isSaved ? 'Re-save Signature' : 'Save Signature'}
        </button>
        <button type="button" disabled={!hasDraft && !isSaved} onClick={clearDraft}>Clear Signature</button>
      </div>
    </section>
  );
}


function DeleteConfirmModal({ target, onCancel, onConfirm }) {
  if (!target) return null;
  const isIncident = target.type === 'incident';
  const count = Number(target.dailyCount || 0);

  return (
    <div className="ics214-modal-backdrop" role="presentation" onMouseDown={event => {
      if (event.target === event.currentTarget) onCancel();
    }}>
      <section className="ics214-modal" role="dialog" aria-modal="true" aria-labelledby="ics214-delete-title">
        <span className="ics214-kicker">CONFIRM DELETE</span>
        <h3 id="ics214-delete-title">{isIncident ? 'Delete Incident?' : 'Delete Daily 214?'}</h3>
        <p>
          {isIncident
            ? <>Delete <strong>{target.label}</strong> and all {count} saved Daily 214{count === 1 ? '' : 's'} for this incident?</>
            : <>Delete <strong>{target.label}</strong> from <strong>{target.incidentLabel}</strong>?</>}
        </p>
        <div className="ics214-modal-warning">This cannot be undone. The saved record{isIncident ? 's' : ''} will be removed from this device.</div>
        <div className="ics214-inline-actions">
          <button type="button" onClick={onCancel}>Cancel</button>
          <button type="button" className="ics214-danger-button" onClick={onConfirm}>
            {isIncident ? 'Delete Incident & All 214s' : 'Delete Daily 214'}
          </button>
        </div>
      </section>
    </div>
  );
}

export default function Ics214({ profile }) {
  const id = memberId(profile);
  const { state: trackingState } = useTrackingStore(profile?.id);
  const [store, setStore] = useState(() => load214State(id));
  const [view, setView] = useState({ type: 'home', incidentId: '', dailyId: '' });
  const [feed, setFeed] = useState([]);
  const [feedStatus, setFeedStatus] = useState('loading');
  const [incidentInput, setIncidentInput] = useState('');
  const [newMedlEmail, setNewMedlEmail] = useState('');
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/incidents', { cache: 'no-store' })
      .then(response => response.ok ? response.json() : Promise.reject(new Error('Incident feed unavailable')))
      .then(payload => {
        if (cancelled) return;
        setFeed(Array.isArray(payload?.incidents) ? payload.incidents : []);
        setFeedStatus('ready');
      })
      .catch(() => {
        if (!cancelled) setFeedStatus('offline');
      });
    return () => { cancelled = true; };
  }, []);

  const persist = updater => {
    setStore(current => {
      const next = typeof updater === 'function' ? updater(current) : updater;
      return save214State(id, next);
    });
  };

  const options = useMemo(() => incidentOptions(feed, trackingState), [feed, trackingState]);
  const activeDeployments = useMemo(
    () => activeDeploymentsForPerson(trackingState, profile?.full_name || ''),
    [trackingState, profile?.full_name]
  );

  const selectedIncident = store.incidents.find(item => item.id === view.incidentId) || null;
  const selectedDaily = selectedIncident?.dailies?.find(item => item.id === view.dailyId) || null;

  const updateIncident = (incidentId, updater) => {
    persist(current => ({
      ...current,
      incidents: current.incidents.map(incident => incident.id === incidentId
        ? { ...(typeof updater === 'function' ? updater(incident) : updater), updatedAt: new Date().toISOString() }
        : incident),
    }));
  };

  const updateDaily = (incidentId, dailyId, updater) => {
    updateIncident(incidentId, incident => ({
      ...incident,
      dailies: (incident.dailies || []).map(daily => daily.id === dailyId
        ? { ...(typeof updater === 'function' ? updater(daily) : updater), updatedAt: new Date().toISOString() }
        : daily),
    }));
  };

  const addIncidentFrom = source => {
    const row = normalizeIncident({
      name: source?.name || source?.incident,
      incidentOrderNumber: source?.incidentOrderNumber,
      incidentIrwinId: source?.incidentIrwinId,
      incidentSource: source?.incidentSource || source?.source,
      medlEmail: newMedlEmail,
    });
    if (!row.name) return setError('Choose or enter an incident.');
    const existing = store.incidents.find(item =>
      item.name.toLowerCase() === row.name.toLowerCase()
      && String(item.incidentOrderNumber || '').toLowerCase() === String(row.incidentOrderNumber || '').toLowerCase()
    );
    if (existing) {
      setIncidentInput('');
      setNewMedlEmail('');
      setView({ type: 'incident', incidentId: existing.id, dailyId: '' });
      return;
    }
    persist(current => ({ ...current, incidents: [row, ...current.incidents] }));
    setIncidentInput('');
    setNewMedlEmail('');
    setView({ type: 'incident', incidentId: row.id, dailyId: '' });
  };

  const addFromPicker = event => {
    event.preventDefault();
    setError('');
    const entered = incidentInput.trim();
    const matched = options.find(option => option.display.toLowerCase() === entered.toLowerCase());
    addIncidentFrom(matched || { name: entered, incidentSource: matched ? matched.incidentSource : 'Manual' });
  };

  const addDaily = incident => {
    const prior = latestDaily(incident);
    const daily = defaultDaily({ profile, prior });
    updateIncident(incident.id, current => ({ ...current, dailies: [daily, ...(current.dailies || [])] }));
    setView({ type: 'daily', incidentId: incident.id, dailyId: daily.id });
  };

  const requestIncidentDelete = incident => {
    setDeleteTarget({
      type: 'incident',
      incidentId: incident.id,
      label: formatIncidentDisplay(incident),
      dailyCount: (incident.dailies || []).length,
    });
  };

  const requestDailyDelete = (incident, daily) => {
    setDeleteTarget({
      type: 'daily',
      incidentId: incident.id,
      dailyId: daily.id,
      incidentLabel: formatIncidentDisplay(incident),
      label: daily.opLabel || daily.fromDate || 'Daily 214',
    });
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;

    if (deleteTarget.type === 'incident') {
      const label = deleteTarget.label;
      persist(current => ({
        ...current,
        incidents: current.incidents.filter(incident => incident.id !== deleteTarget.incidentId),
      }));
      setDeleteTarget(null);
      setError('');
      setNotice(`Deleted ${label} and all saved Daily 214s for that incident.`);
      setView({ type: 'home', incidentId: '', dailyId: '' });
      return;
    }

    const label = deleteTarget.label;
    updateIncident(deleteTarget.incidentId, incident => ({
      ...incident,
      dailies: (incident.dailies || []).filter(daily => daily.id !== deleteTarget.dailyId),
    }));
    setDeleteTarget(null);
    setError('');
    setNotice(`Deleted ${label}.`);
    setView({ type: 'incident', incidentId: deleteTarget.incidentId, dailyId: '' });
  };

  if (view.type === 'home') {
    return (
      <div className="ics214-root">
        <section className="ics214-hero">
          <div>
            <span className="ics214-kicker">FIELD DOCUMENTATION</span>
            <h2>ICS 214 Activity Log</h2>
            <p>Build the day as it happens. Your 214s stay on this device and remain usable without service.</p>
          </div>
          <div className="ics214-offline-pill">Offline Ready</div>
        </section>

        {notice && <div className="ics214-success">{notice}</div>}
        {error && <div className="ics214-error">{error}</div>}

        <section className="ics214-card">
          <div className="ics214-section-head"><div><h3>My 214 Incidents</h3><p>Open an existing incident or add a new one.</p></div></div>
          {!store.incidents.length && <div className="ics214-empty-small">No ICS 214 incidents saved on this device yet.</div>}
          <div className="ics214-incident-list">
            {store.incidents.map(incident => {
              const latest = latestDaily(incident);
              return (
                <div className="ics214-list-row" key={incident.id}>
                  <button type="button" className="ics214-list-open" onClick={() => setView({ type: 'incident', incidentId: incident.id, dailyId: '' })}>
                    <span><strong>{formatIncidentDisplay(incident)}</strong><small>{incident.medlEmail ? `MEDL: ${incident.medlEmail}` : 'MEDL email not saved yet'}</small></span>
                    <span>{latest ? `${latest.opLabel || 'Daily 214'} · ${latest.activities?.length || 0} activities` : 'No daily 214s yet'} →</span>
                  </button>
                  <button type="button" className="ics214-delete-row" onClick={() => requestIncidentDelete(incident)} aria-label={`Delete ${formatIncidentDisplay(incident)} and all Daily 214s`}>Delete</button>
                </div>
              );
            })}
          </div>
        </section>

        <section className="ics214-card">
          <div className="ics214-section-head"><div><h3>+ Add Incident</h3><p>Use your current assignment first, choose an active incident, or enter one manually.</p></div></div>

          {activeDeployments.length > 0 && (
            <div className="ics214-assigned">
              <span className="ics214-kicker">ASSIGNED TO YOU</span>
              {activeDeployments.map(deployment => (
                <button type="button" key={deployment.id} onClick={() => addIncidentFrom(deployment)}>
                  <strong>{deployment.incident}</strong>
                  <small>{deployment.incidentOrderNumber || deployment.deploymentType || deployment.role || 'Current deployment'}</small>
                  <span>Use This Incident →</span>
                </button>
              ))}
            </div>
          )}

          <form className="ics214-form" onSubmit={addFromPicker}>
            <label>Incident
              <IncidentPicker value={incidentInput} onChange={setIncidentInput} options={options} feedStatus={feedStatus} />
            </label>
            <label>MEDL Email <span className="ics214-optional">(optional - save it once for this incident)</span>
              <input type="email" value={newMedlEmail} onChange={event => setNewMedlEmail(event.target.value)} placeholder="medl@agency.gov" />
            </label>
            <button type="submit" className="ics214-primary">Add Incident</button>
          </form>
        </section>
        <DeleteConfirmModal target={deleteTarget} onCancel={() => setDeleteTarget(null)} onConfirm={confirmDelete} />
      </div>
    );
  }

  if (view.type === 'incident' && selectedIncident) {
    const sortedDailies = [...(selectedIncident.dailies || [])].sort((a, b) => Date.parse(b.createdAt || '') - Date.parse(a.createdAt || ''));
    return (
      <div className="ics214-root">
        <button type="button" className="ics214-back" onClick={() => setView({ type: 'home', incidentId: '', dailyId: '' })}>← All 214 Incidents</button>
        <section className="ics214-hero compact">
          <div><span className="ics214-kicker">ICS 214 INCIDENT</span><h2>{formatIncidentDisplay(selectedIncident)}</h2><p>{sortedDailies.length} daily 214{sortedDailies.length === 1 ? '' : 's'} saved on this device.</p></div>
          <button type="button" className="ics214-primary" onClick={() => addDaily(selectedIncident)}>+ Add Daily 214</button>
        </section>

        <section className="ics214-card">
          <div className="ics214-section-head"><div><h3>MEDL Email</h3><p>Saved for this incident so you do not have to type it every day. Change it anytime if the assignment moves to another team.</p></div></div>
          <div className="ics214-medl-row">
            <input
              type="email"
              value={selectedIncident.medlEmail || ''}
              onChange={event => updateIncident(selectedIncident.id, incident => ({ ...incident, medlEmail: event.target.value.trim().toLowerCase() }))}
              placeholder="medl@agency.gov"
            />
          </div>
        </section>

        <section className="ics214-card">
          <div className="ics214-section-head"><div><h3>Daily 214s</h3><p>Open today's log or start a new operational period.</p></div></div>
          {!sortedDailies.length && <div className="ics214-empty-small">No daily 214s yet.</div>}
          <div className="ics214-incident-list">
            {sortedDailies.map(daily => (
              <div className="ics214-list-row" key={daily.id}>
                <button type="button" className="ics214-list-open" onClick={() => setView({ type: 'daily', incidentId: selectedIncident.id, dailyId: daily.id })}>
                  <span>
                    <strong>{daily.opLabel || 'Daily 214'}</strong>
                    <small>{daily.fromDate || 'No date'} {daily.fromTime || ''} - {daily.toDate || ''} {daily.toTime || ''}</small>
                  </span>
                  <span>{daily.activities?.length || 0} activities{daily.lastEmailedAt ? ' · emailed' : ''} →</span>
                </button>
                <button type="button" className="ics214-delete-row" onClick={() => requestDailyDelete(selectedIncident, daily)} aria-label={`Delete ${daily.opLabel || 'Daily 214'}`}>Delete</button>
              </div>
            ))}
          </div>
        </section>
        <DeleteConfirmModal target={deleteTarget} onCancel={() => setDeleteTarget(null)} onConfirm={confirmDelete} />
      </div>
    );
  }

  if (view.type === 'daily' && selectedIncident && selectedDaily) {
    const prior = [...(selectedIncident.dailies || [])]
      .filter(item => item.id !== selectedDaily.id && Date.parse(item.createdAt || '') < Date.parse(selectedDaily.createdAt || ''))
      .sort((a, b) => Date.parse(b.createdAt || '') - Date.parse(a.createdAt || ''))[0] || null;

    const setDaily = updater => updateDaily(selectedIncident.id, selectedDaily.id, updater);
    const saveHeader = () => {
      setError('');
      if (!selectedDaily.opLabel?.trim()) return setError('Enter the operational period.');
      if (!selectedDaily.name?.trim()) return setError('Enter the name/resource designation.');
      if (!selectedDaily.icsPosition?.trim()) return setError('Enter your ICS position.');
      setDaily(daily => ({ ...daily, headerLocked: true }));
      setNotice('214 header saved. Add activities as the day happens.');
    };

    const reusePrevious = () => {
      if (!prior) return;
      setDaily(daily => invalidate214Signature(daily, {
        name: prior.name || daily.name,
        icsPosition: prior.icsPosition || daily.icsPosition,
        homeAgency: prior.homeAgency || daily.homeAgency,
        resources: (prior.resources || []).map(resource => ({ ...resource, id: make214Id('resource') })),
      }));
    };

    return (
      <Daily214
        profile={profile}
        incident={selectedIncident}
        daily={selectedDaily}
        prior={prior}
        setDaily={setDaily}
        updateIncident={updater => updateIncident(selectedIncident.id, updater)}
        onBack={() => setView({ type: 'incident', incidentId: selectedIncident.id, dailyId: '' })}
        saveHeader={saveHeader}
        reusePrevious={reusePrevious}
        notice={notice}
        setNotice={setNotice}
        error={error}
        setError={setError}
      />
    );
  }

  return <div className="ics214-root"><div className="ics214-error">This ICS 214 record could not be opened.</div><button type="button" onClick={() => setView({ type: 'home', incidentId: '', dailyId: '' })}>Back</button></div>;
}

function Daily214({ profile, incident, daily, prior, setDaily, updateIncident, onBack, saveHeader, reusePrevious, notice, setNotice, error, setError }) {
  const [emailTo, setEmailTo] = useState(incident.medlEmail || daily.lastEmailTo || '');
  const [subject, setSubject] = useState(daily.lastEmailSubject || defaultSubject(incident, daily));
  const [emailBusy, setEmailBusy] = useState(false);

  useEffect(() => {
    setEmailTo(incident.medlEmail || daily.lastEmailTo || '');
  }, [incident.medlEmail, daily.lastEmailTo]);

  useEffect(() => {
    if (!daily.lastEmailSubject) setSubject(defaultSubject(incident, daily));
  }, [incident.name, daily.opLabel, daily.name, daily.lastEmailSubject]);

  const updateField = (field, value) => setDaily(current => invalidate214Signature(current, { [field]: value }));
  const generate = () => generateIcs214Pdf({ incident, daily });

  const sharePdf = async () => {
    setError('');
    setNotice('');
    try {
      const bytes = generate();
      await shareOrOpenIcs214(bytes, ics214FileName(incident, daily));
    } catch (shareError) {
      setError(shareError?.message || 'Unable to open the ICS 214 PDF.');
    }
  };

  const emailPdf = async () => {
    setError('');
    setNotice('');
    const recipient = emailTo.trim().toLowerCase();
    if (!recipient) return setError('Enter the MEDL email address.');
    if (!subject.trim()) return setError('Enter an email subject.');
    if (navigator.onLine === false) return setError('Email requires service. Your 214 is still saved on this device.');

    setEmailBusy(true);
    try {
      const token = await getMemberAccessToken();
      if (!token) throw new Error('Member sign in required.');
      const bytes = generate();
      const response = await fetch('/api/ics214-email', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: recipient,
          subject: subject.trim(),
          filename: ics214FileName(incident, daily),
          pdfBase64: pdfBytesToBase64(bytes),
          incidentName: incident.name,
          operationalPeriod: daily.opLabel,
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.error || 'Unable to email the ICS 214.');

      updateIncident(current => ({ ...current, medlEmail: recipient }));
      setDaily(current => ({
        ...current,
        lastEmailedAt: new Date().toISOString(),
        lastEmailTo: recipient,
        lastEmailSubject: subject.trim(),
      }));
      setNotice(`ICS 214 emailed to ${recipient}.`);
    } catch (emailError) {
      setError(emailError?.message || 'Unable to email the ICS 214.');
    } finally {
      setEmailBusy(false);
    }
  };

  return (
    <div className="ics214-root">
      <button type="button" className="ics214-back" onClick={onBack}>← {incident.name}</button>
      <section className="ics214-hero compact">
        <div>
          <span className="ics214-kicker">DAILY ICS 214</span>
          <h2>{daily.opLabel || 'New Daily 214'}</h2>
          <p>{formatIncidentDisplay(incident)}</p>
        </div>
        <div className={`ics214-lock-pill ${daily.headerLocked ? 'locked' : 'editing'}`}>{daily.headerLocked ? 'Header Saved' : 'Editing Header'}</div>
      </section>

      {notice && <div className="ics214-success">{notice}</div>}
      {error && <div className="ics214-error">{error}</div>}

      <section className="ics214-card">
        <div className="ics214-section-head">
          <div><span className="ics214-kicker">214 HEADER</span><h3>Operational Period & Crew</h3><p>Save this once, then spend the rest of the day adding activity entries.</p></div>
          {daily.headerLocked
            ? <button type="button" onClick={() => setDaily(current => invalidate214Signature(current, { headerLocked: false }))}>Edit Header</button>
            : prior && <button type="button" onClick={reusePrevious}>Reuse Previous Crew</button>}
        </div>

        {daily.headerLocked ? (
          <div className="ics214-summary-grid">
            <span><small>Operational Period</small><strong>{daily.opLabel}</strong></span>
            <span><small>From</small><strong>{daily.fromDate} {daily.fromTime}</strong></span>
            <span><small>To</small><strong>{daily.toDate} {daily.toTime}</strong></span>
            <span><small>ICS Position</small><strong>{daily.icsPosition}</strong></span>
            <span><small>Name</small><strong>{daily.name}</strong></span>
            <span><small>Home Agency / Unit</small><strong>{daily.homeAgency || 'Not entered'}</strong></span>
            <span className="wide"><small>Resources Assigned</small><strong>{daily.resources?.length || 0}</strong></span>
          </div>
        ) : (
          <div className="ics214-form">
            <div className="ics214-grid">
              <label>Operational Period<input value={daily.opLabel} onChange={event => updateField('opLabel', event.target.value)} placeholder="Example: OP 7" /></label>
              <label>ICS Position<input value={daily.icsPosition} onChange={event => updateField('icsPosition', event.target.value)} placeholder="EMPF, EMTF, MEDL-T, etc." /></label>
              <label>Date From<input type="date" value={daily.fromDate} onChange={event => updateField('fromDate', event.target.value)} /></label>
              <label>Time From<input type="time" value={daily.fromTime} onChange={event => updateField('fromTime', event.target.value)} /></label>
              <label>Date To<input type="date" value={daily.toDate} onChange={event => updateField('toDate', event.target.value)} /></label>
              <label>Time To<input type="time" value={daily.toTime} onChange={event => updateField('toTime', event.target.value)} /></label>
              <label>Name / Resource Designator<input value={daily.name} onChange={event => updateField('name', event.target.value)} /></label>
              <label>Home Agency / Unit<input value={daily.homeAgency} onChange={event => updateField('homeAgency', event.target.value)} placeholder="Agency / Unit" /></label>
            </div>

            <ResourceEditor resources={daily.resources || []} onChange={resources => updateField('resources', resources)} daily={daily} profile={profile} />
            <button type="button" className="ics214-primary" onClick={saveHeader}>Save 214 Header</button>
          </div>
        )}
      </section>

      {daily.headerLocked && (
        <>
          <ActivityEditor daily={daily} onChange={activities => updateField('activities', activities)} />

          <SignaturePad
            value={daily.signatureStrokes || []}
            signedAt={daily.signedAt || ''}
            onSave={strokes => {
              setDaily(current => ({ ...current, signatureStrokes: strokes, signedAt: new Date().toISOString() }));
              setNotice('214 signature saved.');
              setError('');
            }}
            onClear={() => {
              setDaily(current => ({ ...current, signatureStrokes: [], signedAt: '' }));
              setNotice('');
            }}
          />

          <section className="ics214-card">
            <div className="ics214-section-head"><div><span className="ics214-kicker">SUBMIT / RETAIN</span><h3>PDF & Email</h3><p>The PDF is generated from the ICS 214 data saved on this device. Email requires service.</p></div></div>
            <div className="ics214-email-form">
              <label>MEDL Email<input type="email" value={emailTo} onChange={event => setEmailTo(event.target.value)} placeholder="medl@agency.gov" /></label>
              <label>Subject<input value={subject} onChange={event => setSubject(event.target.value)} /></label>
              <div className="ics214-inline-actions">
                <button type="button" onClick={sharePdf}>Share / Print PDF</button>
                <button type="button" className="ics214-primary" disabled={emailBusy} onClick={emailPdf}>{emailBusy ? 'Sending…' : 'Email 214'}</button>
              </div>
              {daily.lastEmailedAt && <small className="ics214-last-sent">Last emailed {formatWhen(daily.lastEmailedAt)} to {daily.lastEmailTo}.</small>}
            </div>
          </section>
        </>
      )}
    </div>
  );
}
